import React from 'react';
import { Calendar } from 'lucide-react';
import { format, parse, isValid } from 'date-fns';
import { REPORT_ACCENT } from './reportTheme';
import { type ReportRange, rangePresets, minISO, maxISO } from './reportRange';
import CalendarPopover from './steps/CalendarPopover';

// CTV-445: global date-range filter at the top of the report. Presets (All /
// Last 7 / Last 30 days) plus a custom from–to picker, all clamped to the
// campaign's flight window. Emits { from, to } (null = full window) which the
// page threads into every section's fetch.

interface DateRangeFilterProps {
  start: string; // campaign flight start (YYYY-MM-DD)
  end: string; // campaign flight end (YYYY-MM-DD)
  value: ReportRange;
  onChange: (next: ReportRange) => void;
  accentColor?: string;
}

// A compact date chip + the shared branded calendar (same one the wizard uses).
// Small footprint so it fits the filter bar, unlike the wizard's tall field.
const RangeDateField: React.FC<{
  label: string;
  value: string; // YYYY-MM-DD
  onChange: (v: string) => void;
  min: string;
  max: string;
  accent: string;
}> = ({ label, value, onChange, min, max, accent }) => {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef<HTMLButtonElement | null>(null);
  const parsed = parse(value, 'yyyy-MM-dd', new Date());
  const display = isValid(parsed) ? format(parsed, 'MMM d, yyyy') : value;
  return (
    <>
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[12px] text-slate-700 hover:border-slate-300 focus:outline-none focus:ring-1"
        style={{ ['--tw-ring-color' as string]: accent }}
      >
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</span>
        <span className="tabular-nums">{display}</span>
        <Calendar className="w-3 h-3" style={{ color: open ? accent : 'rgb(148, 163, 184)' }} />
      </button>
      <CalendarPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={ref}
        value={value}
        onChange={onChange}
        min={min}
        max={max}
        accentColor={accent}
      />
    </>
  );
};

const DateRangeFilter: React.FC<DateRangeFilterProps> = ({ start, end, value, onChange, accentColor }) => {
  const accent = accentColor || REPORT_ACCENT;

  const presets = rangePresets(start, end);

  const sameRange = (a: ReportRange, b: ReportRange) => a.from === b.from && a.to === b.to;
  const activeKey = presets.find((p) => sameRange(p.range, value))?.key ?? 'custom';

  // The custom inputs default to the full window when a bound is null.
  const fromVal = value.from ?? start;
  const toVal = value.to ?? end;

  const setFrom = (v: string) => {
    const clamped = minISO(maxISO(v, start), toVal); // within [start, to]
    onChange({ from: clamped, to: toVal });
  };
  const setTo = (v: string) => {
    const clamped = maxISO(minISO(v, end), fromVal); // within [from, end]
    onChange({ from: fromVal, to: clamped });
  };

  const pill = (key: string, label: string, range: ReportRange) => {
    const active = activeKey === key;
    return (
      <button
        key={key}
        type="button"
        onClick={() => onChange(range)}
        className="px-2.5 py-1 rounded-full text-[11px] font-medium tracking-tight transition-colors whitespace-nowrap"
        style={active ? { backgroundColor: accent, color: '#fff' } : { color: 'rgb(71, 85, 105)', backgroundColor: 'rgb(241, 245, 249)' }}
        aria-pressed={active}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white px-4 py-2.5 flex items-center gap-3 flex-wrap">
      <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-slate-400">
        <Calendar className="w-3.5 h-3.5" style={{ color: accent }} />
        Date range
      </span>
      <div className="flex items-center gap-1.5">{presets.map((p) => pill(p.key, p.label, p.range))}</div>
      {/* Custom range — compact chips opening the same branded calendar the wizard uses. */}
      <div className="flex items-center gap-2 ml-auto">
        <RangeDateField label="From" value={fromVal} onChange={setFrom} min={start} max={toVal} accent={accent} />
        <span className="text-[12px] text-slate-400">–</span>
        <RangeDateField label="To" value={toVal} onChange={setTo} min={fromVal} max={end} accent={accent} />
      </div>
    </div>
  );
};

export default DateRangeFilter;
